import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
  Button,
  Group,
  Tabs,
  Text,
  TextInput,
  NumberInput,
  Stack,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import { Page, FormSectionCard } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useQuizBasePath } from "../../hooks/useQuizBasePath";
import RichTextEditor from "../../components/richTextEditor";
import QuizParticipantSelector from "../../components/quiz/QuizParticipantSelector";
import QuizDocumentUploader from "../../components/quiz/QuizDocumentUploader";
import QuizQuestionBuilder from "../../components/quiz/QuizQuestionBuilder";

const apiEntity = "quiz";

const defaultValues = {
  title: "",
  body: "",
  startDate: new Date(),
  deadline: null,
  testDuration: 60,
  maxRetakeCount: 100,
  passingRate: 70,
  targetRole: "ALL",
  noLicenceNeed: true,
  requiredLicenses: [],
  requiredSfcLicences: [],
  status: "DRAFT",
};

const QuizEditor = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const basePath = useQuizBasePath();
  const [api] = useServerApi();
  const [userRoles, setUserRoles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [savingAction, setSavingAction] = useState(null);
  const [activeTab, setActiveTab] = useState("basic");

  const form = useForm({ initialValues: defaultValues });

  useEffect(() => {
    let active = true;
    const loadRoles = async () => {
      const res = await api.search({
        apiEntity: "userRole",
        searchQuery: {},
        currentPage: 1,
        pageSize: 200,
      });
      if (active) setUserRoles(res?.docs ?? []);
    };
    loadRoles();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      const quiz = await api.getById({ apiEntity, id });
      if (!quiz) return;
      form.setValues({
        title: quiz.title ?? "",
        body: quiz.body ?? "",
        startDate: quiz.startDate ? new Date(quiz.startDate) : new Date(),
        deadline: quiz.deadline ? new Date(quiz.deadline) : null,
        testDuration: quiz.testDuration ?? 60,
        maxRetakeCount: quiz.maxRetakeCount ?? 100,
        passingRate: quiz.passingRate ?? 70,
        targetRole: Array.isArray(quiz.targetRole)
          ? quiz.targetRole.map(String)
          : quiz.targetRole ?? "ALL",
        noLicenceNeed: quiz.noLicenceNeed !== false,
        requiredLicenses: quiz.requiredLicenses ?? [],
        requiredSfcLicences: quiz.requiredSfcLicences ?? [],
        status: quiz.status ?? "DRAFT",
      });
      setDocuments(quiz.documents ?? []);
      setQuestions(quiz.questions ?? []);
    };
    load();
  }, [id, isNew]);

  useEffect(() => {
    if (activeTab !== "documents" || isNew || !id) return;
    let active = true;
    const reloadDocuments = async () => {
      const quiz = await api.getById({ apiEntity, id });
      if (active && quiz?.documents) setDocuments(quiz.documents);
    };
    reloadDocuments();
    return () => {
      active = false;
    };
  }, [activeTab, id, isNew]);

  const persistQuestions = async (quizId) => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const payload = {
        questionText: q.questionText,
        options: q.options,
        order: i,
      };
      if (q._id) {
        await api.Quiz.updateQuestion(q._id, payload);
      } else {
        const res = await api.Quiz.addQuestion(quizId, payload);
        if (res?.data?._id) questions[i]._id = res.data._id;
      }
    }
    const ids = questions.filter((q) => q._id).map((q) => q._id);
    if (ids.length) await api.Quiz.reorderQuestions(ids);
  };

  const save = async (publish = false) => {
    if (!form.values.title?.trim()) {
      showNotification({ title: "Validation", message: "Title is required", color: "red" });
      return;
    }
    setSavingAction(publish ? "publish" : "save");
    try {
      const payload = { ...form.values };
      let quizId = id;
      let res;
      if (isNew) {
        res = await api.add({ apiEntity, values: payload });
        quizId = res?.data?._id;
      } else {
        res = await api.updateById({ apiEntity, id, values: payload });
      }
      if (!res?.success) throw new Error("Save failed");

      await persistQuestions(quizId);

      if (publish) {
        await api.Quiz.publish(quizId);
        showNotification({ title: "Published", message: "Quiz is now active", color: "green" });
      } else {
        showNotification({ title: "Saved", message: "Quiz saved as draft", color: "green" });
      }
      navigate(`${basePath}/${quizId}/edit`);
    } catch (error) {
      showNotification({ title: "Error", message: error.message ?? "Save failed", color: "red" });
    } finally {
      setSavingAction(null);
    }
  };

  const handleUpload = async (file) => {
    if (isNew) {
      showNotification({ title: "Save first", message: "Save the quiz before uploading documents", color: "orange" });
      return;
    }
    const res = await api.Quiz.uploadDocument(id, file);
    if (res?.data) setDocuments((prev) => [...prev, res.data]);
  };

  const handleOpenDoc = async (doc) => {
    await api.Quiz.downloadDocument(doc._id, doc.fileName);
  };

  const handleDeleteDoc = async (docId) => {
    await api.Quiz.deleteDocument(docId);
    setDocuments((prev) => prev.filter((d) => d._id !== docId));
  };

  const pageTitle = isNew
    ? "New Quiz"
    : form.values.title?.trim()
      ? `Edit Quiz > ${form.values.title.trim()}`
      : "Edit Quiz";

  return (
    <Page
      title={pageTitle}
      actions={
        <Group>
          <Button
            variant="default"
            onClick={() => navigate(basePath)}
            disabled={!!savingAction}
          >
            Cancel
          </Button>
          <Button
            loading={savingAction === "save"}
            disabled={!!savingAction}
            onClick={() => save(false)}
          >
            Save
          </Button>
          <Button
            loading={savingAction === "publish"}
            disabled={!!savingAction}
            color="green"
            onClick={() => save(true)}
          >
            Publish Quiz
          </Button>
        </Group>
      }
    >
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="basic">Basic Info</Tabs.Tab>
          <Tabs.Tab value="participants">Participants</Tabs.Tab>
          <Tabs.Tab value="documents">Documents</Tabs.Tab>
          <Tabs.Tab value="questions">Questions</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="basic">
          <FormSectionCard title="Basic Info">
            <Stack spacing="md">
              <Group grow align="flex-start">
                <TextInput label="Quiz Title" required {...form.getInputProps("title")} />
                <NumberInput
                  label="Passing Rate (%)"
                  min={0}
                  max={100}
                  {...form.getInputProps("passingRate")}
                />
              </Group>
              <Group grow align="flex-start">
                <DatePicker label="Start Date" {...form.getInputProps("startDate")} />
                <DatePicker label="Deadline" {...form.getInputProps("deadline")} />
              </Group>
              <Group grow align="flex-start">
                <NumberInput
                  label={
                    <Group spacing={6} noWrap>
                      <span>Test Duration (minutes)</span>
                      <Text component="span" size="xs" color="dimmed">
                        (0 = no time limit)
                      </Text>
                    </Group>
                  }
                  min={0}
                  {...form.getInputProps("testDuration")}
                />
                <NumberInput
                  label="Max Retake Count"
                  min={1}
                  {...form.getInputProps("maxRetakeCount")}
                />
              </Group>
              <RichTextEditor
                form={form}
                name="body"
                label="Quiz Body / Instructions"
                minRows={10}
              />
            </Stack>
          </FormSectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="participants">
          <FormSectionCard title="Participants">
            <QuizParticipantSelector form={form} userRoles={userRoles} />
          </FormSectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="documents">
          <FormSectionCard title="Documents">
            <QuizDocumentUploader
              documents={documents}
              onUpload={handleUpload}
              onDelete={handleDeleteDoc}
              onOpen={handleOpenDoc}
            />
          </FormSectionCard>
        </Tabs.Panel>

        <Tabs.Panel value="questions">
          <FormSectionCard title="Questions">
            <QuizQuestionBuilder questions={questions} onChange={setQuestions} />
          </FormSectionCard>
        </Tabs.Panel>
      </Tabs>
    </Page>
  );
};

export default QuizEditor;
